// src/pages/RelatorioProducaoSetor.js
import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent,
  TextField, Button, CircularProgress, Alert,
  FormControl, InputLabel, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, TablePagination, Autocomplete
} from '@mui/material';
import {
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  PictureAsPdf as PdfIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const escapeHtml = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));

const RelatorioProducaoSetor = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dados, setDados] = useState([]);
  const [estatisticas, setEstatisticas] = useState(null);
  const [filtros, setFiltros] = useState({
    dataInicio: '',
    dataFim: '',
    horaInicio: '00:00',
    horaFim: '23:59',
    setor: ''
  });
  const [setores, setSetores] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [funcionarioSelecionado, setFuncionarioSelecionado] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Definir período padrão (mês atual)
  useEffect(() => {
    const hoje = new Date();
    const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    const ultimoDia = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);

    setFiltros(prev => ({
      ...prev,
      dataInicio: primeiroDia.toISOString().split('T')[0],
      dataFim: ultimoDia.toISOString().split('T')[0]
    }));

    carregarSetores();
    carregarFuncionariosPorSetor(); // Carregar todos os funcionários inicialmente
  }, []);

  const carregarSetores = async () => {
    try {
      const response = await api.listarSetores();
      if (response.success) {
        setSetores(response.dados || []);
      }
    } catch (error) {
      console.error('Erro ao carregar setores:', error);
    }
  };

  const carregarFuncionariosPorSetor = async (nomeSetor) => {
    try {
      console.log('Carregando funcionários:', nomeSetor ? `do setor ${nomeSetor}` : 'de todos os setores');
      const response = await api.listarFuncionarios(nomeSetor ? { setor: nomeSetor } : {});
      
      if (response.success) {
        if (nomeSetor) {
          // Filtrar funcionários do setor específico
          const funcionariosDoSetor = response.dados.filter(func => func.setor === nomeSetor);
          setFuncionarios(funcionariosDoSetor);
          console.log(`${funcionariosDoSetor.length} funcionários encontrados no setor ${nomeSetor}`);
        } else {
          // Carregar todos os funcionários
          setFuncionarios(response.dados || []);
          console.log(`${response.dados.length} funcionários encontrados (todos os setores)`);
        }
        setFuncionarioSelecionado(null); // Limpar seleção anterior
      } else {
        setFuncionarios([]);
      }
    } catch (error) {
      console.error('Erro ao carregar funcionários:', error);
      setFuncionarios([]);
    }
  };

  const carregarDados = async () => {
    if (!filtros.dataInicio || !filtros.dataFim) {
      setError('Selecione o período (data início e fim)');
      return;
    }

    // Validar se pelo menos um filtro foi selecionado (setor ou funcionário)
    if (!filtros.setor && !funcionarioSelecionado) {
      setError('Selecione um setor ou um funcionário para o relatório');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const parametros = {
        dataInicio: `${filtros.dataInicio} ${filtros.horaInicio}:00`,
        dataFim: `${filtros.dataFim} ${filtros.horaFim}:59`,
        limite: 50000 // Limite alto para pegar todas as requisições
      };

      // Adicionar filtro por setor se selecionado
      if (filtros.setor) {
        parametros.setor = filtros.setor;
      }

      // Adicionar filtro por funcionário se selecionado
      if (funcionarioSelecionado) {
        parametros.funcionario = funcionarioSelecionado.codigo;
      }

      console.log('Carregando requisições do setor:', parametros);

      // Usar o endpoint de leituras que retorna todas as requisições
      const response = await api.relatorioLeituras(parametros);
      
      if (response.success) {
        const leituras = response.dados || [];
        setDados(leituras);
        setEstatisticas(response.meta?.estatisticas || null);
        console.log(`Carregadas ${leituras.length} requisições`);
      } else {
        setError(response.message || 'Erro ao carregar dados');
      }
    } catch (error) {
      console.error('Erro ao carregar relatório:', error);
      setError('Erro ao carregar dados. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const gerarPDF = () => {
    const doc = new jsPDF('l', 'mm', 'a4'); // l = landscape (paisagem)
    
    // Configurações iniciais
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    let yPosition = 15;
    
    // Título do relatório
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.text('Relatório de Produção por Setor', pageWidth / 2, yPosition, { align: 'center' });
    
    yPosition += 10;
    
    // Informações do filtro
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Setor: ${filtros.setor}`, 14, yPosition);
    yPosition += 5;
    
    if (funcionarioSelecionado) {
      doc.text(`Funcionário: ${funcionarioSelecionado.nome} (${funcionarioSelecionado.codigo})`, 14, yPosition);
      yPosition += 5;
    }
    
    doc.text(`Período: ${filtros.dataInicio} ${filtros.horaInicio} até ${filtros.dataFim} ${filtros.horaFim}`, 14, yPosition);
    yPosition += 5;
    
    doc.text(`Data de Geração: ${new Date().toLocaleString('pt-BR')}`, 14, yPosition);
    yPosition += 10;
    
    // Estatísticas (se existirem)
    if (estatisticas) {
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('Resumo Estatístico', 14, yPosition);
      yPosition += 7;
      
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      
      // Criar caixas de estatísticas
      const boxWidth = 60;
      const boxHeight = 15;
      const boxMargin = 5;
      let xPosition = 14;
      
      // Total de Bipadas
      doc.rect(xPosition, yPosition, boxWidth, boxHeight);
      doc.text('Total de Bipadas', xPosition + 2, yPosition + 5);
      doc.setFont(undefined, 'bold');
      doc.text(estatisticas.total_leituras.toString(), xPosition + boxWidth/2, yPosition + 11, { align: 'center' });
      doc.setFont(undefined, 'normal');
      xPosition += boxWidth + boxMargin;
      
      // Requisições Únicas
      doc.rect(xPosition, yPosition, boxWidth, boxHeight);
      doc.text('Requisições Únicas', xPosition + 2, yPosition + 5);
      doc.setFont(undefined, 'bold');
      doc.text(estatisticas.requisicoes_unicas.toString(), xPosition + boxWidth/2, yPosition + 11, { align: 'center' });
      doc.setFont(undefined, 'normal');
      xPosition += boxWidth + boxMargin;
      
      // Funcionários Ativos
      doc.rect(xPosition, yPosition, boxWidth, boxHeight);
      doc.text('Funcionários Ativos', xPosition + 2, yPosition + 5);
      doc.setFont(undefined, 'bold');
      doc.text(estatisticas.funcionarios_distintos.toString(), xPosition + boxWidth/2, yPosition + 11, { align: 'center' });
      doc.setFont(undefined, 'normal');
      xPosition += boxWidth + boxMargin;
      
      // Média por Funcionário
      doc.rect(xPosition, yPosition, boxWidth, boxHeight);
      doc.text('Média por Funcionário', xPosition + 2, yPosition + 5);
      doc.setFont(undefined, 'bold');
      const media = Math.round((estatisticas.total_leituras / estatisticas.funcionarios_distintos) || 0);
      doc.text(media.toString(), xPosition + boxWidth/2, yPosition + 11, { align: 'center' });
      doc.setFont(undefined, 'normal');
      
      yPosition += boxHeight + 10;
    }
    
    // Tabela de dados
    if (dados && dados.length > 0) {
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text(`Detalhamento das Requisições (${dados.length} registros)`, 14, yPosition);
      yPosition += 7;
      
      // Preparar dados para a tabela
      const tableHeaders = ['ID', 'Código Requisição', 'Data/Hora', 'Funcionário', 'Código Func.', 'Setor Efetivo'];
      const tableData = dados.map(requisicao => [
        requisicao.id,
        requisicao.codigo_requisicao,
        requisicao.data_formatada,
        requisicao.funcionario_nome || 'N/A',
        requisicao.codigo_funcionario,
        requisicao.setor_efetivo
      ]);
      
      // Gerar tabela
      doc.autoTable({
        head: [tableHeaders],
        body: tableData,
        startY: yPosition,
        styles: {
          fontSize: 8,
          cellPadding: 2,
          overflow: 'linebreak'
        },
        headStyles: {
          fillColor: [103, 126, 234], // Cor do cabeçalho (similar ao gradiente do header)
          textColor: 255,
          fontStyle: 'bold'
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        margin: { left: 14, right: 14 },
        didDrawPage: function(data) {
          // Adicionar número da página
          doc.setFontSize(8);
          doc.setFont(undefined, 'normal');
          doc.text(
            `Página ${data.pageNumber}`,
            pageWidth / 2,
            pageHeight - 10,
            { align: 'center' }
          );
        }
      });
    }
    
    // Salvar o PDF
    const nomeArquivo = `relatorio_producao_setor_${filtros.setor}_${filtros.dataInicio}_${filtros.dataFim}.pdf`;
    doc.save(nomeArquivo);
  };

  const handlePrint = () => {
    // Criar uma nova janela para impressão
    const printWindow = window.open('', '_blank');
    
    if (!printWindow) {
      alert('Bloqueador de pop-ups impediu a abertura da janela de impressão. Por favor, permita pop-ups para este site.');
      return;
    }
    
    // Gerar HTML para impressão com TODOS os dados
    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Relatório de Produção por Setor - ${filtros.setor || 'Todos'}</title>
          <style>
            @media print {
              @page {
                size: landscape;
                margin: 15mm;
              }
              body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
            
            body {
              font-family: Arial, sans-serif;
              font-size: 11px;
              margin: 0;
              padding: 15px;
              color: #333;
            }
            
            .header {
              text-align: center;
              margin-bottom: 20px;
              border-bottom: 3px solid #667eea;
              padding-bottom: 10px;
            }
            
            h1 {
              color: #667eea;
              font-size: 24px;
              margin: 0 0 10px 0;
            }
            
            .info-section {
              background-color: #f5f5f5;
              padding: 10px;
              border-radius: 5px;
              margin-bottom: 15px;
            }
            
            .info-row {
              display: flex;
              margin-bottom: 5px;
            }
            
            .info-label {
              font-weight: bold;
              min-width: 120px;
            }
            
            .stats-container {
              display: flex;
              gap: 15px;
              margin-bottom: 20px;
              flex-wrap: wrap;
            }
            
            .stat-box {
              flex: 1;
              min-width: 200px;
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              color: white;
              padding: 15px;
              border-radius: 8px;
              text-align: center;
            }
            
            .stat-value {
              font-size: 32px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            
            .stat-label {
              font-size: 12px;
              opacity: 0.9;
            }
            
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 10px;
              margin-top: 15px;
            }
            
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            
            th {
              background-color: #667eea;
              color: white;
              font-weight: bold;
              text-align: center;
            }
            
            tbody tr:nth-child(even) {
              background-color: transparent;
            }
            
            tbody tr:hover {
              background-color: #f0f0f0;
            }
            
            .table-title {
              font-size: 16px;
              font-weight: bold;
              color: #667eea;
              margin-top: 20px;
              margin-bottom: 10px;
            }
            
            .footer {
              margin-top: 30px;
              text-align: center;
              font-size: 10px;
              color: #666;
              border-top: 1px solid #ddd;
              padding-top: 10px;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>📊 Relatório de Produção por Setor</h1>
          </div>
          
          <div class="info-section">
            <div class="info-row">
              <span class="info-label">Setor:</span>
              <span>${escapeHtml(filtros.setor || 'Todos os Setores')}</span>
            </div>
            ${funcionarioSelecionado ? `
              <div class="info-row">
                <span class="info-label">Funcionário:</span>
                <span>${escapeHtml(funcionarioSelecionado.nome)} (${escapeHtml(funcionarioSelecionado.codigo)})</span>
              </div>
            ` : ''}
            <div class="info-row">
              <span class="info-label">Período:</span>
              <span>${filtros.dataInicio} ${filtros.horaInicio} até ${filtros.dataFim} ${filtros.horaFim}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Data de Geração:</span>
              <span>${new Date().toLocaleString('pt-BR')}</span>
            </div>
          </div>
          
          ${estatisticas ? `
            <div class="stats-container">
              <div class="stat-box">
                <div class="stat-value">${estatisticas.total_leituras || 0}</div>
                <div class="stat-label">Total de Bipadas</div>
              </div>
              <div class="stat-box">
                <div class="stat-value">${estatisticas.requisicoes_unicas || 0}</div>
                <div class="stat-label">Requisições Únicas</div>
              </div>
              <div class="stat-box">
                <div class="stat-value">${estatisticas.funcionarios_distintos || 0}</div>
                <div class="stat-label">Funcionários Ativos</div>
              </div>
              <div class="stat-box">
                <div class="stat-value">${Math.round((estatisticas.total_leituras / estatisticas.funcionarios_distintos) || 0)}</div>
                <div class="stat-label">Média por Funcionário</div>
              </div>
            </div>
          ` : ''}
          
          ${dados && dados.length > 0 ? `
            <div class="table-title">Detalhamento das Requisições (${dados.length} registros)</div>
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Código Requisição</th>
                  <th>Data/Hora</th>
                  <th>Funcionário</th>
                  <th>Código Func.</th>
                  <th>Setor Efetivo</th>
                </tr>
              </thead>
              <tbody>
                ${dados.map(req => `
                  <tr>
                    <td style="text-align: center;">${req.id}</td>
                    <td style="text-align: center;">${escapeHtml(req.codigo_requisicao)}</td>
                    <td style="text-align: center;">${escapeHtml(req.data_formatada)}</td>
                    <td>${escapeHtml(req.funcionario_nome || 'N/A')}</td>
                    <td style="text-align: center;">${escapeHtml(req.codigo_funcionario)}</td>
                    <td style="text-align: center;">${escapeHtml(req.setor_efetivo)}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          ` : '<p style="text-align: center; color: #999; margin-top: 30px;">Nenhum registro encontrado.</p>'}
          
          <div class="footer">
            <p>Sistema de Gestão de Produção - Relatório gerado automaticamente</p>
          </div>
          
          <script>
            window.onload = function() {
              window.print();
            };
          </script>
        </body>
      </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  return (
    <Box sx={{ p: 3, minHeight: '100vh' }}>
      {/* Header */}
      <Paper elevation={0} sx={{ 
        p: 3, mb: 3, 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
        color: 'white', 
        borderRadius: 2 
      }}>
        <Box display="flex" alignItems="center">
          <BusinessIcon sx={{ mr: 2, fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Relatório de Produção por Setor
            </Typography>
            <Typography variant="subtitle1" opacity={0.9}>
              Análise detalhada da produção organizada por setor
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Filtros */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filtros
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              label="Data Início"
              type="date"
              value={filtros.dataInicio}
              onChange={(e) => setFiltros(prev => ({ ...prev, dataInicio: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <TextField
              label="Data Fim"
              type="date"
              value={filtros.dataFim}
              onChange={(e) => setFiltros(prev => ({ ...prev, dataFim: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              label="Hora Início"
              type="time"
              value={filtros.horaInicio}
              onChange={(e) => setFiltros(prev => ({ ...prev, horaInicio: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              label="Hora Fim"
              type="time"
              value={filtros.horaFim}
              onChange={(e) => setFiltros(prev => ({ ...prev, horaFim: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Setor (Opcional)</InputLabel>
              <Select
                value={filtros.setor}
                onChange={(e) => {
                  const novoSetor = e.target.value;
                  setFiltros(prev => ({ ...prev, setor: novoSetor }));
                  carregarFuncionariosPorSetor(novoSetor || null); // Carregar funcionários do setor ou todos
                }}
                label="Setor (Opcional)"
              >
                <MenuItem value="">
                  <em>Todos os Setores</em>
                </MenuItem>
                {setores.map(setor => (
                  <MenuItem key={setor.id} value={setor.nome}>
                    {setor.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={funcionarios}
              getOptionLabel={(funcionario) => 
                `${funcionario.nome} (${funcionario.codigo}) - ${funcionario.setor}`
              }
              value={funcionarioSelecionado}
              onChange={(event, newValue) => setFuncionarioSelecionado(newValue)}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Funcionário (Opcional)"
                  helperText={
                    funcionarios.length === 0 
                      ? "Nenhum funcionário encontrado"
                      : filtros.setor
                        ? `${funcionarios.length} funcionário(s) do setor ${filtros.setor}`
                        : `${funcionarios.length} funcionário(s) disponível(is) (todos os setores)`
                  }
                />
              )}
              renderOption={(props, funcionario) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {funcionario.nome}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Código: {funcionario.codigo} | Setor: {funcionario.setor}
                    </Typography>
                  </Box>
                </Box>
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" gap={2}>
              <Button
                variant="contained"
                onClick={carregarDados}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <AssessmentIcon />}
              >
                {loading ? 'Carregando...' : 'Gerar Relatório'}
              </Button>
              
              <Button
                variant="outlined"
                onClick={gerarPDF}
                disabled={loading || dados.length === 0}
                startIcon={<PdfIcon />}
                color="error"
              >
                Exportar PDF
              </Button>
              
              <Button
                variant="outlined"
                onClick={handlePrint}
                disabled={loading || dados.length === 0}
                startIcon={<PrintIcon />}
                color="primary"
              >
                Imprimir
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Estatísticas do Setor */}
      {estatisticas && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              📊 Resumo {funcionarioSelecionado ? `do Funcionário: ${funcionarioSelecionado.nome}` : `do Setor: ${filtros.setor}`}
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                variant="text"
                onClick={gerarPDF}
                startIcon={<PdfIcon />}
                color="error"
                size="small"
              >
                Exportar PDF
              </Button>
              <Button
                variant="text"
                onClick={handlePrint}
                startIcon={<PrintIcon />}
                color="primary"
                size="small"
              >
                Imprimir
              </Button>
            </Box>
          </Box>
          {funcionarioSelecionado && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Funcionário: {funcionarioSelecionado.nome} (Código: {funcionarioSelecionado.codigo}) | Setor: {funcionarioSelecionado.setor}
            </Typography>
          )}
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="primary.main" fontWeight="bold">
                    {estatisticas.total_leituras}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total de Bipadas
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="success.main" fontWeight="bold">
                    {estatisticas.requisicoes_unicas}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Requisições Únicas
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="warning.main" fontWeight="bold">
                    {estatisticas.funcionarios_distintos}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Funcionários Ativos
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="info.main" fontWeight="bold">
                    {Math.round((estatisticas.total_leituras / estatisticas.funcionarios_distintos) || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Média por Funcionário
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Tabela de Requisições */}
      {dados.length > 0 && (
        <Paper>
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="h6" fontWeight="bold">
              📋 Todas as Requisições Bipadas ({dados.length} registros)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Setor: {filtros.setor}
              {funcionarioSelecionado && (
                <> | Funcionário: {funcionarioSelecionado.nome} ({funcionarioSelecionado.codigo})</>
              )}
              <br />
              Período: {filtros.dataInicio} {filtros.horaInicio} até {filtros.dataFim} {filtros.horaFim}
            </Typography>
          </Box>
          
          <TableContainer sx={{ maxHeight: '70vh' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Código Requisição</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Data/Hora</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Funcionário</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Setor Efetivo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dados.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((requisicao, index) => (
                  <TableRow 
                    key={requisicao.id} 
                    hover
                    sx={{
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {requisicao.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={requisicao.codigo_requisicao}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {requisicao.data_formatada}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <PersonIcon sx={{ mr: 1, fontSize: 16, color: 'info.main' }} />
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {requisicao.funcionario_nome || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Cód: {requisicao.codigo_funcionario}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={requisicao.setor_efetivo}
                        color={requisicao.setor_efetivo === filtros.setor ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={dados.length}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100, 250]}
            labelRowsPerPage="Registros por página:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
            }
          />
        </Paper>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Carregando dados...</Typography>
        </Box>
      )}

      {/* Empty State */}
      {!loading && dados.length === 0 && !error && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <TrendingUpIcon sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Nenhum dado encontrado
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Ajuste os filtros e tente novamente
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default RelatorioProducaoSetor;